import { useEffect, useState } from "react";
import { DeviceMobile, X, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const DISMISS_KEY = "ag_pwa_install_dismissed_at";
const DISMISS_DAYS = 30;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function dismissedRecently() {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!t) return false;
    const ageDays = (Date.now() - t) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch { return false; }
}

/**
 * Sprint G · Batch 4 — Prompt nativo de instalación PWA.
 *
 * - Captura `beforeinstallprompt` (Chrome/Edge/Android) y muestra una barra
 *   inferior persuasiva.
 * - Botón "Instalar" llama a `event.prompt()` y registra la respuesta.
 * - Botón "Cerrar" / rechazo guarda `ag_pwa_install_dismissed_at` con timestamp
 *   y el prompt no vuelve durante 30 días.
 * - Auto-oculta si la app ya corre en standalone (instalada).
 */
export function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
      try { localStorage.removeItem(DISMISS_KEY); } catch {}
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!show || !deferred) return null;

  const onInstall = async () => {
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      // outcome: 'accepted' | 'dismissed'
      if (choice?.outcome !== "accepted") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
      }
    } catch {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    } finally {
      setShow(false);
      setDeferred(null);
    }
  };

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[calc(100vw-1.5rem)] sm:w-auto sm:max-w-md
        bg-white text-[#1A1A1A] dark:bg-[#1A1D26] dark:text-white
        border border-gray-200 dark:border-white/10
        rounded-2xl shadow-2xl shadow-amber-900/20
        px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 backdrop-blur"
      data-testid="pwa-install-prompt"
      role="dialog"
      aria-label="Instalar AutoGlow"
    >
      <div className="shrink-0 w-12 h-12 grid place-items-center rounded-xl
        bg-gradient-to-br from-[#FAF0D7] to-[#F5E1A4]
        dark:from-[#2A2417] dark:to-[#3A2E0E]">
        <DeviceMobile size={22} weight="duotone" className="text-[#B8860B] dark:text-[#F5C76A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold leading-tight">Instala AutoGlow</div>
        <p className="text-[11px] mt-0.5 hidden sm:block text-gray-500 dark:text-gray-400">
          Acceso rápido · notificaciones · modo offline.
        </p>
      </div>
      <Button
        size="sm"
        onClick={onInstall}
        className="rounded-xl font-bold shrink-0 shadow-md shadow-amber-900/20 transition
          bg-gradient-to-r from-[#D4AF37] to-[#B8860B] hover:from-[#B8860B] hover:to-[#996F0A]
          dark:from-[#F5C76A] dark:to-[#D4AF37] dark:hover:from-[#D4AF37] dark:hover:to-[#B8860B]
          text-[#1A1A1A] h-9 px-3.5"
        data-testid="pwa-install-cta"
      >
        <DownloadSimple size={14} className="mr-1.5" weight="bold" /> Instalar
      </Button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar"
        className="p-1.5 text-[var(--ag-text-muted)] hover:text-gray-700 dark:hover:text-white shrink-0"
        data-testid="pwa-install-dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
